import React from 'react';
import { Provider, useSelector } from 'react-redux';
import { Route, Routes } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { ConfigProvider } from 'antd';
import { StyleSheetManager } from 'styled-components';
import AnnotationModal from '@components/annotations/AnnotationModal';
import { PageNotFound, Retry } from '@components/common';
import { isInternalUserSelector, selectedCustomerIdSelector } from '@store/common/selectors';
import PulsePopupModal from '@components/pulse-announcement-modal';
import Feedback from '@components/feedback';
import {
  CURRENT_PRODUCT_MODE,
  isOdpModule,
  isAppModule,
  PRODUCT_MODE_MAP,
  CONVIVA_LEARNING_CENTER_CUSTOMER_ID,
  ROOT_SCROLL_CONTAINER_ELEMENT_ID,
} from '@utils/constants';
import { FilterManagement } from '@components/filter';
import { DimensionManagement } from '@components/dimension-management';
import { UsageDashboardPage, ApiUsageBreakdownPage, AutoInsights } from '../pages';

import { useInitialization } from '../hooks';
import store from '../store';
import theme from './localTheme';
import OdpRouter from './Odp';
import 'antd/dist/reset.css';
import '../styles/index.less';
import EmailSubscriptionsPage from '@src/components/notification-channel/EmailSubscriptions';
import GlobalMutePage from '@src/components/notification-channel/GlobalMute';
import AlertRulesPage from '@src/components/notification-channel/AlertRules';
import AppRouter from './AppRouter';
import VideoAdRouter from './VideoAdRouter';
import { NavApp, initNavStore, ENVIRONMENTS, PRODUCTS } from '@pulse/nav';
import QuickTour from '@src/components/QuickTour';
import { useAtomValue } from 'jotai';
import { coreFetchedAtom, queryClient } from './utils';
import CoreHooks from './CoreHooks';
import { UserManagement } from '@src/components/user-management';
import ServiceIntegrations from '@src/components/notification-channel/ServiceIntegrations';
import ThrottledDataWarning from '@src/components/common/ThrottledDataWarning';
import { initDashboardStorage } from '@src/utils/dashboard/db';

const { baseUrl, module } = window.conviva;

// figure out standalone once, at module level
const params = new URLSearchParams(window.location.search);
const IS_STANDALONE = params.get('standalone') === 'true';

// for those users without user profile,
//  we need them to redirect to profile page without any checks ( auth, profile, etc)
// so in case of standalone, it will not load nav-app component
// only run nav/db init in normal mode
if (!IS_STANDALONE) {
  initNavStore({
    product: module as PRODUCTS,
    env: process.env.NODE_CONFIG_ENV as ENVIRONMENTS,
  });

  initDashboardStorage();
}

const LiteApp = () => {
  return (
    <>
      {[PRODUCT_MODE_MAP.EI, PRODUCT_MODE_MAP.AI].includes(CURRENT_PRODUCT_MODE) && (
        <VideoAdRouter />
      )}
    </>
  );
};

export const App = () => {
  const [initialized, error] = useInitialization();
  const selectedCustomerId = useSelector(selectedCustomerIdSelector);
  const isInternalUser = useSelector(isInternalUserSelector);
  const isFetched = useAtomValue(coreFetchedAtom);

  if (!initialized || !isFetched) {
    return <Retry isLoading showError={error !== null} />;
  }
  // When application is switched in Apphub, history is not updated properly.
  // Following code will correct the history.
  // TODO: is this required?
  // if (window.location.pathname !== history.location.pathname) {
  //   history.replace(window.location.pathname);
  // }

  if (selectedCustomerId === CONVIVA_LEARNING_CENTER_CUSTOMER_ID && !isInternalUser) {
    return <PageNotFound />;
  }

  return (
    <>
      <ReactQueryDevtools initialIsOpen={false} />
      {/*
      // Apphub/Nav package already has a customer check call
        <CheckActiveCustomer />
      */}
      {isAppModule && <AppRouter />}

      {[PRODUCT_MODE_MAP.EI, PRODUCT_MODE_MAP.AI].includes(CURRENT_PRODUCT_MODE) && (
        <VideoAdRouter />
      )}

      {isOdpModule && (
        <Routes>
          <Route path={`${baseUrl}/*`} element={<OdpRouter />} />
        </Routes>
      )}

      <QuickTour />
      <Feedback />
      <AutoInsights />
      <AnnotationModal />
      <PulsePopupModal />
      <FilterManagement />
      <UserManagement />
      <DimensionManagement />
      <EmailSubscriptionsPage />
      <GlobalMutePage />
      <AlertRulesPage />
      <ServiceIntegrations />
      <UsageDashboardPage />
      <ApiUsageBreakdownPage />
    </>
  );
};

// disabling CSSOM to fix qiankun issues
// eslint-disable-next-line react/display-name
export default ({ container }: { container: HTMLElement }) => {
  return (
    <StyleSheetManager disableCSSOMInjection={true} target={container}>
      <Provider store={store as any}>
        <ConfigProvider
          theme={theme}
          getPopupContainer={(): any => document.getElementById('ifc-portals')}
        >
          <QueryClientProvider client={queryClient}>
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              {!IS_STANDALONE && <NavApp />}
              {!IS_STANDALONE && <CoreHooks />}
              <ThrottledDataWarning />
              <div
                id={ROOT_SCROLL_CONTAINER_ELEMENT_ID}
                style={{
                  overflowY: 'scroll',
                  flex: 1,
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                {IS_STANDALONE ? <LiteApp /> : <App />}
              </div>
            </div>
          </QueryClientProvider>
        </ConfigProvider>
      </Provider>
    </StyleSheetManager>
  );
};
